"use client";

import { Vehicle } from "@alba-cars/common-modules";
import { motion, useInView } from "framer-motion";
import React, { FC, JSX, memo, useEffect, useRef } from "react";

import { useIntersectionObserver } from "../_hooks/useIntersectionObserver";
import { useFilterContext } from "./context/FilterContext";
import { useSharedCarsQuery } from "./context/QueryContext";
import ExchangeCarCard from "./ExchangeCarCard";
import RequestCallbackCard from "./RequestCallbackCard";
import CarsCard from "@/components/ui/CarsCard";
import CarsGridLoader from "@/components/ui/loaders/buy-cars/CarsGridLoader";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { chunkArray } from "@/lib/utils";

const MemoizedCarCard = memo(({ car }: { car: Vehicle }) => (
  <article className="h-full">
    <CarsCard car={car} />
  </article>
));
MemoizedCarCard.displayName = "MemoizedCarCard";

const rowVariants = {
  hidden: (isEven: boolean) => ({
    x: isEven ? "10%" : "-10%",
    opacity: 0,
  }),
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      duration: 0.3,
    },
  },
};

// Single animated row component with shuffling effect
const AnimatedRow = ({ cars, index }: { cars: Vehicle[]; index: number }) => {
  const rowRef = useRef(null);
  const isFirstRender = useRef(true);

  const isInView = useInView(rowRef, {
    once: false,
    amount: 0.1,
    initial: true,
  });

  useEffect(() => {
    // After first render, set isFirstRender to false
    isFirstRender.current = false;
  }, []);

  const isSmallerDevice = useMediaQuery("(max-width: 768px)");

  const isEven = index % 2 === 0;

  if (isSmallerDevice) {
    return (
      <div ref={rowRef} className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cars.map((car) => (
          <div key={car.id} role="listitem" aria-label={`${car.make?.name} ${car.model?.name} ${car.year}`}>
            <MemoizedCarCard car={car} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      ref={rowRef}
      className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
      initial={isFirstRender.current ? "visible" : "hidden"}
      animate={isInView ? "visible" : "hidden"}
      variants={rowVariants}
      custom={isEven}
    >
      {cars.map((car) => (
        <motion.div
          key={car.id}
          role="listitem"
          aria-label={`${car.make?.name} ${car.model?.name} ${car.year}`}
          variants={cardVariants}
        >
          <MemoizedCarCard car={car} />
        </motion.div>
      ))}
    </motion.div>
  );
};

interface CarsGridProps {
  pageSize?: number;
  /**
   * Render ExchangeCarCard after this many rows have been rendered.
   * Default is 3 (after the third row).
   */
  exchangeCardAfterRows?: number;
  /**
   * Render RequestCallbackCard after this many rows have been rendered.
   * Default is 6 (after the sixth row).
   */
  requestCallbackAfterRows?: number;
}

const CarsGrid: FC<CarsGridProps> = ({ exchangeCardAfterRows = 3, requestCallbackAfterRows = 6 }) => {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSharedCarsQuery();
  const { isStateUpdating } = useFilterContext();

  useIntersectionObserver({
    target: loadMoreRef,
    onIntersect: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    enabled: !!hasNextPage && !isFetchingNextPage,
  });

  const allCars = data?.pages?.flatMap((page) => page?.data?.result ?? []) ?? [];

  const isLaptopOrSmaller = useMediaQuery("(max-width: 1024px)");

  if (isLoading || isStateUpdating) {
    return (
      <div aria-live="polite" aria-busy="true">
        <CarsGridLoader />
      </div>
    );
  }

  const SIZE = isLaptopOrSmaller ? 2 : 3;

  const rows = chunkArray(allCars, SIZE);

  const renderRows: JSX.Element[] = [];
  rows.forEach((row, index) => {
    renderRows.push(
      <section key={`row-${index}`} role="list" aria-label={`Cars row ${index + 1}`}>
        <AnimatedRow cars={row} index={index} />
      </section>,
    );

    if (index + 1 === exchangeCardAfterRows) {
      renderRows.push(
        <React.Fragment key="exchange-card">
          <section className="hidden lg:block" aria-label="Exchange Car Offer" role="complementary">
            <ExchangeCarCard variant="desktop" />
          </section>
          <section className="lg:hidden" aria-label="Exchange Car Offer" role="complementary">
            <ExchangeCarCard variant="mobile" />
          </section>
        </React.Fragment>,
      );
    }

    if (index + 1 === requestCallbackAfterRows) {
      renderRows.push(
        <section key="callback-card" aria-label="Request Callback" role="complementary">
          <RequestCallbackCard />
        </section>,
      );
    }
  });

  return (
    <div className="overflow-x-hidden">
      {renderRows}

      {/* Load more trigger */}
      <div
        ref={loadMoreRef}
        className="h-10"
        aria-hidden="true"
        aria-live="polite"
        aria-busy="true"
        aria-label="Loading more cars"
      />
    </div>
  );
};

export default CarsGrid;
